import './App.css';
import './i18n';  // Initialize i18n
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import OfflineIndicator from './components/OfflineIndicator';
import GlucoseLog from './pages/GlucoseLog';
import MealLog from './pages/MealLog';
import MedicationReminders from './pages/MedicationReminders';
import AIInsights from './pages/AIInsights';
import Recipes from './pages/Recipes';
import Profile from './pages/Profile';
import AdminDashboard from './pages/AdminDashboard';
import AdminLogin from './pages/admin/AdminLogin';
import AdminProducts from './pages/admin/AdminProducts';
import AdminRecipes from './pages/admin/AdminRecipes';
import Pricing from './pages/Pricing';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import CookiePolicy from './pages/CookiePolicy';
import Contact from './pages/Contact';
import Blog from './pages/Blog';
import Emotions from './pages/Emotions';
import Vitals from './pages/Vitals';
import Analytics from './pages/Analytics';
import Exercise from './pages/Exercise';
import HealthTracking from './pages/HealthTracking';
import Community from './pages/Community';
import CareTeam from './pages/CareTeam';
import Reports from './pages/Reports';
import SmartFeatures from './pages/SmartFeatures';
import FoodScanner from './pages/FoodScanner';
import Shop from './pages/Shop';
import PaymentSuccess from './pages/PaymentSuccess';
import AuthCallback from './pages/AuthCallback';
import LandingPage from './pages/LandingPage';
import Achievements from './pages/Achievements';
import Challenges from './pages/Challenges';
import Referral from './pages/Referral';
import WeeklyReport from './pages/WeeklyReport';
import AIFeatures from './pages/AIFeatures';
import Layout from './components/Layout';
import { ThemeProvider } from './context/ThemeContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LanguageProvider } from './context/LanguageContext';
import PrivacySettings from './pages/PrivacySettings';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

// App Router with Auth Callback Detection
const AppRouter = () => {
  const location = useLocation();
  
  // Check URL fragment for session_id (Google OAuth callback)
  // This must be synchronous, before useEffect runs
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return <AppRoutes />;
};

// App Routes Component
const AppRoutes = () => {
  const { isAuthenticated, user, logout } = useAuth();
  
  return (
    <Routes>
      {/* Landing Page - Public */}
      <Route path="/" element={<LandingPage />} />
      
      <Route 
        path="/login" 
        element={
          isAuthenticated ? 
          <Navigate to="/dashboard" replace /> : 
          <Login />
        } 
      />
      
      {/* Auth Callback Route */}
      <Route path="/auth/callback" element={<AuthCallback />} />
      
      {/* Public Policy Pages (no auth required) */}
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/cookie-policy" element={<CookiePolicy />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/emotions" element={<Emotions />} />
      <Route path="/vitals" element={<Vitals />} />
      
      {/* Admin Routes (no Layout) */}
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin/dashboard" element={<AdminDashboard />} />
      <Route path="/admin/products" element={<AdminProducts />} />
      <Route path="/admin/recipes" element={<AdminRecipes />} />
      
      {/* User Routes (with Layout) */}
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <Layout user={user} onLogout={logout}>
              <Routes>
                <Route path="/dashboard" element={<Dashboard user={user} />} />
                <Route path="/glucose" element={<GlucoseLog />} />
                <Route path="/meals" element={<MealLog />} />
                <Route path="/medications" element={<MedicationReminders />} />
                <Route path="/exercise" element={<Exercise />} />
                <Route path="/health" element={<HealthTracking />} />
                <Route path="/analytics" element={<Analytics />} />
                <Route path="/insights" element={<AIInsights />} />
                <Route path="/smart" element={<SmartFeatures />} />
                <Route path="/food-scanner" element={<FoodScanner />} />
                <Route path="/recipes" element={<Recipes />} />
                <Route path="/community" element={<Community />} />
                <Route path="/care-team" element={<CareTeam />} />
                <Route path="/reports" element={<Reports />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/payment/success" element={<PaymentSuccess />} />
                <Route path="/shop" element={<Shop />} />
                <Route path="/profile" element={<Profile user={user} />} />
                <Route path="/privacy" element={<PrivacySettings />} />
                <Route path="/achievements" element={<Achievements />} />
                <Route path="/challenges" element={<Challenges />} />
                <Route path="/referral" element={<Referral />} />
                <Route path="/weekly-report" element={<WeeklyReport />} />
                <Route path="/ai-features" element={<AIFeatures />} />
              </Routes>
            </Layout>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
};

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <ThemeProvider>
          <div className="App">
            <BrowserRouter>
              <AppRouter />
              <OfflineIndicator />
            </BrowserRouter>
          </div>
        </ThemeProvider>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
